import { useEffect, useState } from 'react';
import { Check, Loader2, Trash2 } from 'lucide-react';
import { useSettings, useUpdateSettings } from '@/api/settings';
import { useClearSecret, useSecretStatus, useSetSecret } from '@/api/practiceSecrets';
import { usePayers } from '@/api/payers';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { FormField, Input } from '@/components/ui/FormField';
import { OABulkOptInDialog } from './OABulkOptInDialog';
import { OADisableWarningDialog } from './OADisableWarningDialog';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
/*
 * Configuration → Billing → Submission Settings. Office Ally submitter id,
 * SFTP host/port/user, and the default test-mode toggle. The SFTP password
 * is env-only (OA_SFTP_PASSWORD on the server) — we deliberately do not
 * store it here.
 *
 * Lands in settings.billing. Shallow-merge patch so the unrelated keys
 * (authExpiryDays etc.) stay intact.
 */

interface FormState {
    oaSubmitterId: string;
    oaTestMode: boolean;
    oaSftpHost: string;
    oaSftpPort: string;
    oaSftpUser: string;
}

const EMPTY: FormState = {
    oaSubmitterId: '', oaTestMode: true, oaSftpHost: '', oaSftpPort: '22', oaSftpUser: '',
};

function isConfigured(state: { oaSubmitterId: string; oaSftpHost: string; oaSftpUser: string }): boolean {
    return Boolean(state.oaSubmitterId.trim() && state.oaSftpHost.trim() && state.oaSftpUser.trim());
}

export function SubmissionSettingsPanel() {
    const { data, isLoading, isError } = useSettings();
    const updateM = useUpdateSettings();
    const { data: payers } = usePayers();

    const [form, setForm] = useState<FormState>(EMPTY);
    const [err, setErr] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);
    const [optInOpen, setOptInOpen] = useState(false);
    const [disableWarnOpen, setDisableWarnOpen] = useState(false);

    useEffect(() => {
        if (!data?.billing) return;
        const b = data.billing;
        setForm({
            oaSubmitterId: b.oaSubmitterId ?? '',
            oaTestMode: b.oaTestMode ?? true,
            oaSftpHost: b.oaSftpHost ?? '',
            oaSftpPort: String(b.oaSftpPort ?? 22),
            oaSftpUser: b.oaSftpUser ?? '',
        });
    }, [data]);

    function set<K extends keyof FormState>(k: K, v: FormState[K]) {
        setSaved(false);
        setErr(null);
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function persistSettings(): Promise<void> {
        const portNum = Number(form.oaSftpPort);
        await updateM.mutateAsync({
            type: 'billing',
            updates: {
                oaSubmitterId: form.oaSubmitterId.trim().toUpperCase(),
                oaTestMode: form.oaTestMode,
                oaSftpHost: form.oaSftpHost.trim(),
                oaSftpPort: form.oaSftpPort ? portNum : 22,
                oaSftpUser: form.oaSftpUser.trim(),
            },
        });
        setSaved(true);
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        setSaved(false);

        const portNum = Number(form.oaSftpPort);
        if (form.oaSftpPort && (!Number.isInteger(portNum) || portNum < 1 || portNum > 65535)) {
            setErr('Port must be an integer between 1 and 65535.');
            return;
        }
        if (form.oaSubmitterId && !/^[A-Z0-9]{2,15}$/.test(form.oaSubmitterId.toUpperCase())) {
            setErr('Submitter ID must be 2–15 letters or digits.');
            return;
        }

        // Detect the OA configured/un-configured transition so we can trigger
        // the right side-effect dialog. `data?.billing` is the server's
        // last-saved state; `form` is the draft we're about to write.
        const wasConfigured = isConfigured({
            oaSubmitterId: data?.billing?.oaSubmitterId ?? '',
            oaSftpHost:    data?.billing?.oaSftpHost    ?? '',
            oaSftpUser:    data?.billing?.oaSftpUser    ?? '',
        });
        const willBeConfigured = isConfigured(form);

        try {
            if (wasConfigured && !willBeConfigured) {
                // configured → not: warn about currently-Electronic payers
                // first, let the dialog run the bulk flip + save atomically.
                setDisableWarnOpen(true);
                return;
            }
            await persistSettings();
            if (!wasConfigured && willBeConfigured) {
                // not → configured: prompt the admin to opt payers in.
                setOptInOpen(true);
            }
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        }
    }

    if (isLoading) {
        return (
            <div className="p-12 flex items-center justify-center text-text-subtle">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
        );
    }
    if (isError) {
        return <div className="p-12 text-center text-error">Couldn't load settings.</div>;
    }

    return (
        <div>
            <div className="mb-1">
                <h2 className="font-display text-lg font-bold text-text">Office Ally Submission</h2>
            </div>
            <p className="text-xs text-text-subtle mb-3">
                Office Ally electronic-claim delivery. SFTP password is stored in the server
                environment (OA_SFTP_PASSWORD) and is never entered here.
            </p>

            <Card className="p-5 max-w-3xl">
                <form onSubmit={handleSubmit}>
                    <FormField label="Submitter ID" hint="Office Ally assigned submitter/login id">
                        {(id) => (
                            <Input
                                id={id}
                                value={form.oaSubmitterId}
                                onChange={(e) => set('oaSubmitterId', e.target.value.toUpperCase().slice(0, 15))}
                                placeholder="SUBMITTER"
                                className="font-mono"
                            />
                        )}
                    </FormField>

                    <label className="flex items-center gap-2 mb-4 text-sm text-text cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.oaTestMode}
                            onChange={(e) => set('oaTestMode', e.target.checked)}
                            className="h-4 w-4"
                        />
                        <span>
                            Default to <strong>test-mode</strong> submissions
                            <span className="text-text-subtle"> — filenames get the OATEST keyword so Office Ally parses but does not adjudicate</span>
                        </span>
                    </label>

                    <div className="grid grid-cols-[1fr,120px] gap-3">
                        <FormField label="SFTP host">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.oaSftpHost}
                                    onChange={(e) => set('oaSftpHost', e.target.value)}
                                    placeholder="sftp.officeally.com"
                                    className="font-mono"
                                />
                            )}
                        </FormField>
                        <FormField label="Port">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.oaSftpPort}
                                    onChange={(e) => set('oaSftpPort', e.target.value.replace(/\D/g, '').slice(0, 5))}
                                    inputMode="numeric"
                                    placeholder="22"
                                />
                            )}
                        </FormField>
                    </div>

                    <FormField label="SFTP user">
                        {(id) => (
                            <Input
                                id={id}
                                value={form.oaSftpUser}
                                onChange={(e) => set('oaSftpUser', e.target.value)}
                                className="font-mono"
                            />
                        )}
                    </FormField>

                    {err && <p className="text-sm text-error mb-2">{err}</p>}
                    {saved && <p className="text-sm text-success mb-2">Saved.</p>}

                    <div className="flex justify-end">
                        <Button variant="primary" size="sm" type="submit" disabled={updateM.isPending}>
                            {updateM.isPending ? 'Saving…' : 'Save'}
                        </Button>
                    </div>
                </form>
            </Card>

            <SftpPasswordCard />

            <OABulkOptInDialog
                payers={payers ?? []}
                open={optInOpen}
                onOpenChange={setOptInOpen}
            />
            <OADisableWarningDialog
                payers={payers ?? []}
                open={disableWarnOpen}
                onOpenChange={setDisableWarnOpen}
                onConfirmed={persistSettings}
            />
        </div>
    );
}

/*
 * Write-only password control. The server never returns the stored
 * value; we only know whether one is set and when it was last updated.
 * Entering a new value replaces the stored one; Remove deletes it.
 */
function SftpPasswordCard() {
    const { data: status, isLoading } = useSecretStatus('oa-sftp-password');
    const setM = useSetSecret('oa-sftp-password');
    const clearM = useClearSecret('oa-sftp-password');

    const [value, setValue] = useState('');
    const [err, setErr] = useState<string | null>(null);
    const [justSaved, setJustSaved] = useState(false);
    const [confirmClearOpen, setConfirmClearOpen] = useState(false);

    const hasValue = Boolean(status?.hasValue);

    async function handleSave(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        setJustSaved(false);
        if (!value) {
            setErr('Enter a password to save.');
            return;
        }
        try {
            await setM.mutateAsync(value);
            setValue('');
            setJustSaved(true);
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        }
    }

    async function performClear() {
        setErr(null);
        setJustSaved(false);
        try {
            await clearM.mutateAsync();
            setConfirmClearOpen(false);
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not remove.'));
        }
    }

    return (
        <Card className="p-5 max-w-3xl mt-4">
            <div className="mb-3">
                <h3 className="font-display text-base font-bold text-text">SFTP password</h3>
                <p className="text-xs text-text-subtle">
                    Stored encrypted on the server and never returned to the browser. Update by entering
                    a new value below.
                </p>
            </div>

            <div className="mb-4 flex items-center gap-2 text-sm">
                {isLoading ? (
                    <span className="text-text-subtle inline-flex items-center gap-1.5">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                    </span>
                ) : hasValue ? (
                    <>
                        <Check className="w-4 h-4 text-success-text" />
                        <span className="text-text">
                            Password is set
                            {status?.updatedAt && (
                                <span className="text-text-subtle">
                                    {' — last updated '}
                                    {new Date(status.updatedAt).toLocaleString(undefined, {
                                        year: 'numeric', month: 'short', day: '2-digit',
                                        hour: '2-digit', minute: '2-digit',
                                    })}
                                </span>
                            )}
                        </span>
                    </>
                ) : (
                    <span className="text-warning-text">No password stored — live submissions will fail.</span>
                )}
            </div>

            <form onSubmit={handleSave}>
                <FormField label={hasValue ? 'New password' : 'Password'}>
                    {(id) => (
                        <Input
                            id={id}
                            type="password"
                            autoComplete="new-password"
                            value={value}
                            onChange={(e) => { setValue(e.target.value); setJustSaved(false); setErr(null); }}
                            placeholder="••••••••"
                        />
                    )}
                </FormField>

                {err && <p className="text-sm text-error mb-2">{err}</p>}
                {justSaved && <p className="text-sm text-success mb-2">Saved.</p>}

                <div className="flex justify-between items-center">
                    {hasValue ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            onClick={() => { setErr(null); setConfirmClearOpen(true); }}
                            disabled={clearM.isPending}
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                            {clearM.isPending ? 'Removing…' : 'Remove'}
                        </Button>
                    ) : <span />}
                    <Button variant="primary" size="sm" type="submit" disabled={setM.isPending || !value}>
                        {setM.isPending ? 'Saving…' : hasValue ? 'Update password' : 'Save password'}
                    </Button>
                </div>
            </form>

            <Dialog
                open={confirmClearOpen}
                onOpenChange={(o) => { if (!clearM.isPending) setConfirmClearOpen(o); }}
            >
                <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>Remove SFTP password?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-text-muted">
                        Submissions will fail until a new password is set.
                    </p>
                    {err && <p className="mt-2 text-xs text-error">{err}</p>}
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button
                            variant="danger"
                            size="sm"
                            type="button"
                            onClick={performClear}
                            disabled={clearM.isPending}
                        >
                            {clearM.isPending ? 'Removing…' : 'Remove'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
